import { createContext, useState, ReactNode, useContext } from 'react';
import { useEnvironment } from "@keycloak/keycloak-ui-shared";
import {usePromise} from "../utils/usePromise";
import {getPersonalInfo} from "../api/methods";
import {UserRepresentation} from "../api/representations";
import {Environment} from "../environment";

export type PersonalInfo = UserRepresentation & {
    accountType?: string;
    profileType?: string;
    profileSubType?: string;
};

export const PersonalInfoContext = createContext<PersonalInfo | null>(null);

export function usePersonalInfoContext() {
    const context = useContext(PersonalInfoContext);

    if (!context) {
        throw new Error('PersonalInfoContext must be used within PersonalInfoProvider');
    }

    return context;
}

interface PersonalInfoProviderProps {
    children: ReactNode;
}

export const PersonalInfoProvider = ({ children }: PersonalInfoProviderProps) => {
    const personalInfo = usePersonalInfo();

    return <PersonalInfoContext.Provider value={personalInfo}>{children}</PersonalInfoContext.Provider>;
};

function usePersonalInfo(): PersonalInfo {
    const context = useEnvironment<Environment>();
    const [personalInfo, setPersonalInfo] = useState<UserRepresentation>();

    usePromise(
        (signal) => getPersonalInfo({signal, context}),
        (personalInfo) => setPersonalInfo(personalInfo)
    );

    return {
        accountType: personalInfo?.attributes?.accountType?.toString(),
        profileType: personalInfo?.attributes?.profileType?.toString(),
        profileSubType: personalInfo?.attributes?.profileSubType?.toString(),
        ...(personalInfo || {})
    };
}